import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

interface ForgotPasswordProps {
  onSwitchToSignIn: () => void
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onSwitchToSignIn }) => {
  const { t } = useTranslation('auth')
  const { language } = useLanguage()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const { requestPasswordReset } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Supabase appends the recovery token to this URL when the user clicks
      // the link in the email. It must be listed under Redirect URLs in the
      // Supabase dashboard (Authentication > URL Configuration).
      const redirectTo = `${window.location.origin}/${language}/reset-password`
      const { error } = await requestPasswordReset(email, redirectTo)

      if (error) {
        setError(error.message)
      } else {
        setSuccess(true)
      }
    } catch (err) {
      console.error('Unexpected error requesting password reset:', err)
      setError(t('errors.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            {t('forgotPassword.successTitle')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mb-6">
            {t('forgotPassword.successMessage', { email })}
          </p>

          <Button onClick={onSwitchToSignIn} variant="outline" className="w-full">
            {t('forgotPassword.backToSignIn')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {t('forgotPassword.title')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600">{t('forgotPassword.subtitle')}</p>
        </div>

        {/* Request Reset Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('forgotPassword.emailLabel')}
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('forgotPassword.emailPlaceholder')}
              required
              className="w-full"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-800 hover:bg-gray-900 disabled:bg-gray-300"
          >
            {loading ? t('forgotPassword.submitting') : t('forgotPassword.submitButton')}
          </Button>
        </form>

        {/* Switch back to Sign In */}
        <div className="text-center mt-6">
          <p className="text-gray-600">
            <button
              onClick={onSwitchToSignIn}
              className="text-gray-800 font-medium hover:text-gray-900 transition-colors"
            >
              {t('forgotPassword.backToSignIn')}
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword
